import styled from '@emotion/styled';
import { useEventLogging } from '@innovamat/event-logging';
import { useFlag } from '@innovamat/flags';
import type { Classroom } from '@innovamat/ga-features';
import {
  APP_PATHS,
  CatchUpDashboard,
  ERROR_TYPE,
  ErrorPage,
  FluencyDashboard,
  GeneralProgress,
  KnowMoreDrawer,
  LAST_YEAR_REPORTS_URL,
  WeeklyResults,
  useClassroom,
  useClassroomCapabilities,
  useCurrentNavigationValues,
  useNavigation,
  useReportsFullScreen,
  useUser,
} from '@innovamat/ga-features';
import { Button, Typography } from '@innovamat/glimmer-components';
import type { LastYearReportsQuery } from '@innovamat/glow-api-client';
import {
  useCourses,
  useLastYearReportsQuery,
} from '@innovamat/glow-api-client';
import type { Stage } from '@innovamat/radiance-utils';
import { dates, stages, standards } from '@innovamat/radiance-utils';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import HeaderReports from './header-reports';

type BaseProps = {
  title: string;
  description: string;
  descriptionTestId: string;
};

type HeaderReportsProps = BaseProps & {
  actions: JSX.Element;
  descriptionLink: string;
};

const Container = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints.xl}) {
    max-width: ${({ theme }) => theme.breakpoints.xl};
    margin: 0 auto;
    display: block;
  }
`;

const TableContainer = styled.div`
  position: relative;
`;

const Actions = styled.div`
  margin-right: 24px;
  margin-bottom: 10px;
`;

export function ReportsNavigationWrapper(): JSX.Element {
  const { t } = useTranslation();
  const { submenu, classroomId: id } = useParams();

  const { setEventData } = useEventLogging();
  const { currentClassroom, courseOrder, currentRoute } =
    useCurrentNavigationValues();
  const { user } = useUser();
  const { courses } = useCourses({
    regionCode: user?.region!,
    organizationId: user?.organizationId!,
  });
  const { navigateInSameNavType, goToResource } = useNavigation();
  const location = useLocation();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { classroom } = useClassroom({
    classroomId: currentClassroom?.id || '',
  });
  const { classroom: canHaveFluencyReports } = useClassroomCapabilities({
    classroom: classroom as Classroom,
  });

  const { value: standardsReportsEnabled, loading } = useFlag(
    'standardsreportsenabled',
    false,
    {
      identifier: '',
      custom: {
        schoolId: user?.organizationId || '',
        region: user?.region || '',
      },
    }
  );

  const { value: lastYearReportBannerFlag } = useFlag(
    'lastYearReportBanner',
    false,
    {
      identifier: '',
      custom: {
        region: user?.region ?? '',
      },
    }
  );

  const { value: useMockData } = useFlag('schoolDemoMockReports', false, {
    identifier: '',
    custom: {
      schoolId: user?.organization?.id || '',
    },
  });

  const { data: lastYearReportsData } =
    useLastYearReportsQuery<LastYearReportsQuery>(
      {
        id: id!,
      },
      {
        enabled: !!id && lastYearReportBannerFlag,
      }
    );

  const hasLastYearReport =
    lastYearReportBannerFlag &&
    (lastYearReportsData?.lastYearReports?.find(
      (lastYearReport) => lastYearReport?.isLastYear === true
    )?.academicYear ||
      false);

  const standard = useMemo(() => {
    if (!loading) {
      return standardsReportsEnabled && user?.organization?.standard
        ? user.organization.standard
        : '';
    }
    return null;
  }, [loading, standardsReportsEnabled, user?.organization?.standard]);

  const educationalStage = stages.getStageFromOrder(courseOrder || 0) as Stage;
  const isMiddleSchoolCourse =
    standard !== standards.BUBBLE_TREE && courseOrder && courseOrder >= 11;
  const isSecundariaCourse =
    stages.isSecundaria(courseOrder) || isMiddleSchoolCourse;
  const isPrimaryCourse = stages.isPrimaria(courseOrder) && !isSecundariaCourse;
  const isEarlyYearsCourse = stages.isInfantil(courseOrder);

  const handleSendEvent = (event: string, parameters?: {}): void => {
    setEventData(event, {
      classroom_id: id,
      ...parameters,
    });
  };

  const handleGoToStudents = (): void => {
    navigateInSameNavType(APP_PATHS.CLASSROOM_STUDENTS_URL, '?tab=rti');
    handleSendEvent('catchup_manage_students_button');
  };

  const handleNavigate = (route: string, queryParams?: string): void => {
    navigateInSameNavType(route, queryParams);
  };

  const handleNavigateToResource = (appletId: string): void => {
    goToResource({
      resourceId: appletId,
      type: 'applet',
      openInNewTab: true,
    });
  };

  const locationInfo = {
    search: location.search,
    pathname: location.pathname,
  };

  const isLastYearReport = currentRoute === LAST_YEAR_REPORTS_URL;
  const isFluencyReports = submenu === 'fluency';

  const dayOfWeekNumber = dates
    .getDaysOfWeek(t)
    .find(
      (day) => day.value === classroom?.digitalPracticeFirstDayOfWeek
    )?.numericDay;

  const getReports = (): JSX.Element => {
    if (!id || !currentClassroom) return <></>;
    switch (submenu) {
      case 'weekly-results':
        return (
          <WeeklyResults
            setEventData={setEventData}
            isSecundaria={!!isSecundariaCourse}
            isEarlyYears={!!isEarlyYearsCourse}
            educationalStage={educationalStage}
            courses={courses}
            handleNavigateToResource={handleNavigateToResource}
            handleNavigate={handleNavigate}
            locationInfo={locationInfo}
            classroom={currentClassroom}
            hasLastYearReport={hasLastYearReport}
            isLastYearReport={isLastYearReport}
          />
        );
      case 'general-progress':
      case 'last-year':
        return (
          <GeneralProgress
            setEventData={setEventData}
            classroomId={id}
            isSecundaria={!!isSecundariaCourse}
            classroomName={currentClassroom?.name || ''}
            educationalStage={educationalStage}
            courses={courses}
            standard={standard}
            handleNavigate={handleNavigate}
            handleNavigateToResource={handleNavigateToResource}
            isLastYearReport={isLastYearReport}
            isPrimary={!!isPrimaryCourse}
            hasLastYearReport={hasLastYearReport}
            dayOfWeekNumber={dayOfWeekNumber}
          />
        );
      case 'intervention':
        return (
          <CatchUpDashboard
            classroom={currentClassroom}
            setEventData={setEventData}
            useMockData={useMockData}
          />
        );
      case 'fluency':
        if (canHaveFluencyReports) {
          return <FluencyDashboard classroomId={id} />;
        } else {
          navigateInSameNavType(APP_PATHS.CLASSROOM_URL);
          return <></>;
        }

      default:
        return <ErrorPage errorType={ERROR_TYPE.NOT_FOUND} />;
    }
  };

  const titles = {
    'weekly-results': t('reports.weeklyResults'),
    'general-progress': t('reports.generalProgress'),
    intervention: t('reports.catch-up-dashboard.header.title'),
    'last-year': t('reports.classroomLastYearReport.title'),
    fluency: t('fluency.reports.menuSection'),
  };

  const descriptions = {
    intervention: t('reports.catch-up.description'),
    'last-year': t('reports.classroomLastYearReport.body'),
  };

  const getTitleForSubmenu = (
    submenu: string,
    isSecundariaCourse: boolean
  ): string | undefined => {
    if (isLastYearReport && isSecundariaCourse) {
      return '';
    }

    if (submenu in titles) {
      return titles[submenu as keyof typeof titles];
    }
    return undefined;
  };

  const getDescriptionForSubmenu = (
    submenu: string,
    isSecundariaCourse: boolean
  ): string | undefined => {
    if (isLastYearReport && isSecundariaCourse) {
      return '';
    }
    if (submenu in descriptions) {
      return descriptions[submenu as keyof typeof descriptions];
    }
    return undefined;
  };

  const getHeaderReportsProps = (): HeaderReportsProps | BaseProps => {
    const baseProps = {
      title: getTitleForSubmenu(submenu!, !!isSecundariaCourse) || '',
      description:
        getDescriptionForSubmenu(submenu!, !!isSecundariaCourse) || '',
      descriptionTestId: 'INTRE_DESCRIPTION',
    };

    if (submenu === 'intervention') {
      return {
        ...baseProps,
        actions: (
          <Button
            onClick={handleGoToStudents}
            variant="secondary"
            dataTestId="INTRE_MANAGESTUDENTSBUTTON"
          >
            {t('reports.catch-up-dashboard.button.manageStudents')}
          </Button>
        ),
        descriptionLink: t('reports.catch-up.description.link'),
      };
    }

    return baseProps;
  };
  const { closeFullScreen } = useReportsFullScreen();

  const showKnowMoreInfo = isPrimaryCourse || isEarlyYearsCourse;

  useEffect(() => {
    if (submenu === 'general-progress') closeFullScreen();
  }, [submenu]);

  return (
    <>
      {showKnowMoreInfo && (
        <KnowMoreDrawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
        />
      )}
      <Container>
        <HeaderReports
          actions={
            <>
              {showKnowMoreInfo && !isFluencyReports && (
                <Actions>
                  <Typography.Link2
                    onClick={() => {
                      setIsDrawerOpen(true);
                    }}
                  >
                    {t('reports.personalizedPractice.knowMore.title')}
                  </Typography.Link2>
                </Actions>
              )}
            </>
          }
          {...getHeaderReportsProps()}
        />
        <TableContainer id="reports-table-container">
          {getReports()}
          <div id="portal-root" />
        </TableContainer>
      </Container>
    </>
  );
}
